import { intakeSiteId } from '@axilon-apps/domains-ontology-ui-state'
import { siteOptionLabel, slugifyId } from './siteIds'

/** Sentinel for the landing site filter: show every run / require a concrete pick for files. */
export const ALL_SITES_FILTER = '__all__'

export interface SiteOption {
  id: string
  name: string
}

/**
 * Storage / job site ids that may have been used for the same logical site.
 * Ingest historically wrote S3 keys and job.siteId as free-text slugs, while
 * intake jobs use the stable UUID derived from the graph Site URN. Listing
 * must try every representation.
 */
export function alternateSiteStorageIds(siteId: string, siteName?: string): string[] {
  const ids: string[] = []
  const push = (value: string | undefined) => {
    const trimmed = value?.trim()
    if (!trimmed || trimmed === '-' || trimmed === '—' || ids.includes(trimmed)) return
    ids.push(trimmed)
  }
  push(siteId)
  if (siteName?.trim()) {
    push(slugifyId(siteName))
    push(siteName.trim())
  }
  if (siteId.includes(':')) {
    push(intakeSiteId(siteId))
    const tail = siteId.split(':').pop()
    push(tail)
    if (tail) push(slugifyId(tail))
  }
  return ids
}

/** True when a job/run site id belongs to the selected filter site. */
export function jobBelongsToSiteFilter(
  jobSiteId: string | undefined,
  filterSiteId: string,
  filterSiteName?: string
): boolean {
  if (!filterSiteId || filterSiteId === ALL_SITES_FILTER) return true
  const candidates = new Set(alternateSiteStorageIds(filterSiteId, filterSiteName))
  const job = jobSiteId?.trim()
  if (!job || job === '-' || job === '—') return false
  if (candidates.has(job)) return true
  // Job stored a slug/name that matches the selected site's name slug.
  if (filterSiteName && slugifyId(filterSiteName) === slugifyId(job)) return true
  return false
}

function siteOptionScore(site: SiteOption): number {
  let score = 0
  if (site.id.includes(':')) score += 2
  if (site.name.trim() && site.name.trim() !== site.id) score += 1
  return score
}

/** True when two picker rows are the same logical site (URN ↔ slug ↔ name). */
export function sameLogicalSite(a: SiteOption, b: SiteOption): boolean {
  if (a.id === b.id) return true
  if (jobBelongsToSiteFilter(a.id, b.id, b.name)) return true
  if (jobBelongsToSiteFilter(b.id, a.id, a.name)) return true
  const aSlug = slugifyId(a.name || a.id)
  const bSlug = slugifyId(b.name || b.id)
  return Boolean(aSlug) && aSlug === bSlug
}

/**
 * Collapse URN / slug / display-name aliases into one dropdown row.
 * Prefers a graph URN id and a human name over a bare job slug.
 */
export function dedupeSiteOptions(sites: SiteOption[]): SiteOption[] {
  const out: SiteOption[] = []
  for (const site of sites) {
    const id = site.id?.trim()
    // This is the organization-level placeholder, not an ingestable plant site.
    if (!id || id === '-' || id === '—' || id === 'urn:org:default') continue
    const next: SiteOption = {
      id,
      name: siteOptionLabel(site.name, id)
    }
    const idx = out.findIndex((existing) => sameLogicalSite(existing, next))
    if (idx < 0) {
      out.push(next)
      continue
    }
    const existing = out[idx]!
    out[idx] = {
      id: siteOptionScore(next) >= siteOptionScore(existing) ? next.id : existing.id,
      name:
        next.name !== next.id
          ? next.name
          : existing.name !== existing.id
            ? existing.name
            : next.name
    }
  }
  return out.sort((a, b) => a.name.localeCompare(b.name))
}
